import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { toast } from "react-hot-toast";
import uploadImage from "../../../utils/uploadImage";
import {
  useGetCoopQuery,
  useGetPublicCompulsoryFeesQuery,
  useGetPublicBranchesQuery,
  useInitiateSignupPaymentMutation,
} from "../../../store/auth/authApi";
import { setCoopData, setCompanyId } from "../../../store/auth/index";
import { persistThemeFromCoopPayload } from "../../../theme/companyTheme";
import { Alert, AuthShell } from "../../../components/Auth";
import PublicSignupWizard, {
  clearSignupDraft,
  formatNaira,
} from "../../../components/PublicSignup/PublicSignupWizard";

const REFERRAL_STORAGE_KEY = "pending_signup_referred_by_member_no";
const SIGNUP_POINTS = [
  "Four short steps: about you, contact, next of kin, then payment.",
  "Your registration fee is paid online through Paystack.",
];

export default function PublicMemberRegistrationPage() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [fullhost, setFullhost] = useState("");
  const [referredByMemberNo, setReferredByMemberNo] = useState("");
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    setFullhost(window.location.host);
  }, []);

  // Capture ?ref= (or referred_by_member_no) and keep it across the Paystack redirect
  useEffect(() => {
    const fromQuery =
      searchParams.get("ref") || searchParams.get("referred_by_member_no") || "";
    const trimmed = String(fromQuery).trim();
    if (trimmed) {
      setReferredByMemberNo(trimmed);
      try {
        sessionStorage.setItem(REFERRAL_STORAGE_KEY, trimmed);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      const stored = sessionStorage.getItem(REFERRAL_STORAGE_KEY);
      if (stored?.trim()) {
        setReferredByMemberNo(stored.trim());
      }
    } catch {
      /* ignore */
    }
  }, [searchParams]);

  const hasReferralInvite = useMemo(
    () => Boolean(referredByMemberNo?.trim()),
    [referredByMemberNo],
  );

  const {
    data: coopData,
    isLoading: isLoadingCoop,
    isSuccess: isCoopSuccess,
    isError: isCoopError,
  } = useGetCoopQuery({ domain_url: fullhost }, { skip: !fullhost });

  const companyDetails = coopData?.data;
  const companyId = companyDetails?.company_id;

  useEffect(() => {
    if (isCoopSuccess && companyDetails) {
      dispatch(setCoopData(companyDetails));
      dispatch(setCompanyId(companyId));
      persistThemeFromCoopPayload(companyDetails);
    }
  }, [isCoopSuccess, companyDetails, companyId, dispatch]);

  const { data: regAmount = {}, isLoading: isLoadingFee } =
    useGetPublicCompulsoryFeesQuery(companyId, { skip: !companyId });

  const { data: branches = [], isLoading: isLoadingBranches } =
    useGetPublicBranchesQuery(fullhost, { skip: !fullhost });

  const [initiatePayment] = useInitiateSignupPaymentMutation();

  /** Sends the member to Paystack. Same payload and storage keys as before the redesign. */
  const handleFormSubmit = async (values) => {
    const {
      imgFile,
      selectedBreakdown,
      selectedBreakdownWithAmounts,
      selectedAmount,
      payment_method,
      ...formikValues
    } = values;

    setSubmitError("");

    if (!companyId) {
      const message = "We could not load this cooperative. Refresh the page and try again.";
      setSubmitError(message);
      toast.error(message);
      return;
    }

    if (payment_method !== "PAYSTACK") {
      const message = "Only online payment (card, through Paystack) is available for sign-up.";
      setSubmitError(message);
      toast.error(message);
      return;
    }

    const totalAmount = selectedAmount ?? regAmount?.total ?? 0;
    if (!totalAmount || totalAmount <= 0) {
      const message = "Select at least one registration fee.";
      setSubmitError(message);
      toast.error(message);
      return;
    }

    let photoUrl =
      "https://www.gravatar.com/avatar/a208380389f40731d3ac10839ec143a2?d=identicon&r=pg&s=100";
    if (imgFile) {
      try {
        photoUrl = await uploadImage(imgFile);
      } catch (error) {
        console.error("Image upload error:", error);
      }
    }

    const memberData = {
      ...formikValues,
      company_id: companyId,
      branch_id: formikValues.branch_id || null,
      amount: totalAmount,
      breakdown:
        selectedBreakdownWithAmounts?.length > 0
          ? selectedBreakdownWithAmounts
          : selectedBreakdown,
      payment_method: "PAYSTACK",
      photo: photoUrl,
    };

    if (!memberData.member_no?.trim()) {
      delete memberData.member_no;
    }

    const referralNo = referredByMemberNo?.trim();
    if (referralNo) {
      memberData.referred_by_member_no = referralNo;
    }

    try {
      sessionStorage.setItem("pending_signup_data", JSON.stringify(memberData));

      const paymentResponse = await initiatePayment({
        ...memberData,
        email: memberData.email || memberData.phone,
        amount: totalAmount,
      }).unwrap();

      if (!paymentResponse || paymentResponse.status === false) {
        const message = paymentResponse?.message || "We could not start the payment.";
        setSubmitError(message);
        sessionStorage.removeItem("pending_signup_data");
        return;
      }

      const paymentUrl =
        paymentResponse?.payment_url || paymentResponse?.data?.payment_url;
      const paymentReference =
        paymentResponse?.payment_reference ||
        paymentResponse?.data?.payment_reference;

      if (paymentUrl && paymentReference) {
        sessionStorage.setItem("pending_signup_payment_reference", paymentReference);
        const feeBreakdown =
          paymentResponse?.fee_breakdown || paymentResponse?.data?.fee_breakdown;
        const totalToPay = feeBreakdown?.member_payable_total || totalAmount;

        toast.success(`Taking you to Paystack to pay ${formatNaira(totalToPay)}`);
        clearSignupDraft();
        window.location.href = paymentUrl;
        // Keep the button busy while the browser leaves the page.
        await new Promise(() => {});
      } else {
        setSubmitError("We did not get a payment link. Please try again.");
        sessionStorage.removeItem("pending_signup_data");
      }
    } catch (error) {
      console.error("Payment initialization error:", error);
      setSubmitError(
        error?.data?.message ||
          error?.response?.data?.message ||
          "We could not start the payment. Check your connection and try again.",
      );
      sessionStorage.removeItem("pending_signup_data");
    }
  };

  if (isCoopError || (!isLoadingCoop && fullhost && !companyDetails)) {
    return (
      <AuthShell company={null}>
        <div className="ax-card">
          <div>
            <h1 className="ax-h1">We could not find this cooperative</h1>
            <p className="ax-sub">
              The registration page for this address did not load. Check the link, or try again in a
              moment.
            </p>
          </div>
          <Link className="ax-link" to="/login">
            <FiArrowLeft aria-hidden="true" size={16} />
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  const branchList = Array.isArray(branches) ? branches : [];

  return (
    <AuthShell
      company={companyDetails}
      loading={isLoadingCoop}
      branches={branchList}
      points={SIGNUP_POINTS}
      wide
    >
      <div className="ax-topbar">
        <Link className="ax-link" to="/login">
          <FiArrowLeft aria-hidden="true" size={16} />
          Back to sign in
        </Link>
        <span className="ax-hint">
          Already a member?{" "}
          <Link className="ax-link" to="/login">
            Sign in
          </Link>
        </span>
      </div>

      {hasReferralInvite ? (
        <Alert tone="info">A member of this cooperative invited you to join.</Alert>
      ) : null}

      <PublicSignupWizard
        branches={branchList}
        isLoadingBranches={isLoadingBranches}
        fees={Array.isArray(regAmount?.break_down) ? regAmount.break_down : []}
        isLoadingFees={isLoadingFee || (!companyId && isLoadingCoop)}
        onSubmit={handleFormSubmit}
        submitError={submitError}
      />
    </AuthShell>
  );
}
